import React, { useState, useEffect, useContext } from 'react';
import PageHeader from "@common/PageHeader";
import Button from "@common/Button";
import TableAntD from "@common/TableAntD";
import { Space, Form, Select, Input, Button as AntdButton, Popconfirm } from "antd";
import useCurrentShortcut from "@hooks/useCurrentShortcut";
import { UserContext } from "@context/UserContext";
import * as lessonPlanApi from './services/lessonPlanApi';
import {
    AddchartRounded,
    ArchiveRounded,
    EditRounded,
    SendRounded
} from "@mui/icons-material";
import Tooltip from "@common/Tooltip";
import { DownloadRounded } from "@mui/icons-material/";
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import { useLocation } from 'wouter';

const LessonPlanManagerDevelopment = () => {
    const { loadShortcutByUrl } = useCurrentShortcut();
    const { user } = useContext(UserContext);
    const [, navigate] = useLocation();
    const [lessonPlans, setLessonPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [nameFilter, setNameFilter] = useState('');
    const [yearFilter, setYearFilter] = useState('All');
    const [displayFilter, setDisplayFilter] = useState('All');

    const fetchLessonPlans = async () => {
        setIsLoading(true);
        try {
            const response = await lessonPlanApi.searchLessonPlans();
            setLessonPlans(response?.content || []);
        } catch (error) {
            console.error('Failed to load lesson plans:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchLessonPlans();
    }, []);

    const handleEdit = (record) => {
        navigate(`/web/ui/view/lessonPlans/plans/${record.key}?editPlan=true`);
    };

    const handleExport = async (record) => {
        try {
            const { blob, filename } = await lessonPlanApi.downloadWordDocument(record.key);

            // Create a download link and trigger it
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export lesson plan:', error);
        }
    };

    const handleAssign = (record) => {
        console.log('Assign:', record);
        // TODO: Implement export functionality
    };

    const handleArchive = async (record) => {
        try {
            await lessonPlanApi.archiveLessonPlans({ keys: [record.key] });
            // Remove the archived plan from the local state
            setLessonPlans(prev => prev.filter(p => p.key !== record.key));
            displayToastMessage('Lesson plan archived successfully', null, 'success', null, 5000);
        } catch (error) {
            console.error('Failed to archive lesson plan:', error);
            displayToastMessage(error.message || 'Failed to archive lesson plan', null, 'error', null, 10000);
        }
    };

    const handleOpenPlan = (record) => {
        navigate(`/web/ui/view/lessonPlans/plans/${record.key}`);
    };

    // Get unique years from lesson plans
    const uniqueYears = [...new Set(lessonPlans.map(plan => plan.yearLevel))].sort();
    const yearOptions = [
        { label: 'All', value: 'All' },
        ...uniqueYears.map(year => ({ label: year, value: year }))
    ];

    const displayOptions = [
        { label: 'All', value: 'All' },
        { label: 'Archived', value: 'Archived' }
    ];

    // Filter the lesson plans based on the selected filters
    const filteredLessonPlans = lessonPlans.filter(plan => {
        const matchesName = !nameFilter ||
            plan.contentName?.toLowerCase().includes(nameFilter.toLowerCase()) ||
            plan.lessonName?.toLowerCase().includes(nameFilter.toLowerCase());
        const matchesYear = yearFilter === 'All' || plan.yearLevel === yearFilter;
        const matchesDisplay = displayFilter === 'All' ||
            (displayFilter === 'Archived' && plan.isArchived);

        return matchesName && matchesYear && matchesDisplay;
    });

    const columns = [
        {
            title: 'Lesson Plan',
            dataIndex: 'contentName',
            key: 'contentName',
            render: (text, record) => (
                <div className={'ch-lpm-table-row-title'} direction="vertical" size={0}>
                    <AntdButton type={'link'} onClick={() => handleOpenPlan(record)} style={{ padding: 0, height: 'auto', textAlign: 'left', display: 'block' }}>
                        <strong>{text}</strong>
                    </AntdButton>
                    <p className={'ch-lpm-table-row-subtitle'}>{record.lessonName}</p>
                </div>
            )
        },
        { title: 'Year', dataIndex: 'yearLevel', key: 'yearLevel' },
        { title: 'Duration', dataIndex: 'timeframe', key: 'timeframe' },
        {
            title: 'Date Created',
            dataIndex: 'createDate',
            key: 'createDate',
            render: (text, record) => user?.dateTimeFormat?.format(record.createDate) || text
        },
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => (
                <div className={'ch-table-controls'}>
                    <Tooltip title={'Export Plan'}>
                        <Button
                            onClick={() => handleExport(record)}
                            ariaLabel={`Export ${text} plan`}
                            type={'text'}
                        >
                            <DownloadRounded />
                        </Button>
                    </Tooltip>
                    <Tooltip title={`Archive Plan`}>
                        <Button
                            onClick={() => handleAssign(record)}
                            ariaLabel={`Assign ${text} plan`}
                            type={'text'}
                        >
                            <SendRounded />
                        </Button>
                    </Tooltip>
                    <Tooltip title={'Edit Plan'}>
                        <Button
                            onClick={() => handleEdit(record)}
                            ariaLabel={`Edit ${text} plan`}
                            type={'text'}
                        >
                            <EditRounded />
                        </Button>
                    </Tooltip>
                    <Popconfirm
                        title="Archive lesson plan?"
                        description="Are you sure you want to archive this lesson plan?"
                        onConfirm={() => handleArchive(record)}
                        okText="Archive"
                        cancelText="Cancel"
                    >
                        <Tooltip title={'Archive Plan'}>
                            <Button
                                ariaLabel={`Archive ${text} plan`}
                                type={'text'}
                            >
                                <ArchiveRounded />
                            </Button>
                        </Tooltip>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    return (
        <div>
            <PageHeader
                title={'Lesson Plans'}
                subtitle={'Use AI to generate lesson plans for your classes based on Cambridge textbook content.'}
                buttons={
                    <>
                        <Button
                            onClick={() => loadShortcutByUrl('/web/ui/view/lessonPlans/createLessonPlan')}
                            icon={<AddchartRounded />}
                        >
                            Create new plan
                        </Button>
                    </>
                }
            />
            <TableFilterControls
                filterContent={
                    <TableFilterControlsForm>
                        <TableFilterControlsSection showDivider={false}>
                            <Form.Item label={<span>Name</span>} htmlFor="name-filter">
                                <Input
                                    id="name-filter"
                                    placeholder="Search by name"
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                    allowClear
                                />
                            </Form.Item>

                            <Form.Item label={<span>Year</span>} htmlFor="year-filter">
                                <Select
                                    id="year-filter"
                                    placeholder="Select year"
                                    options={yearOptions}
                                    value={yearFilter}
                                    onChange={setYearFilter}
                                />
                            </Form.Item>

                            <Form.Item label={<span>Display</span>} htmlFor="display-filter">
                                <Select
                                    id="display-filter"
                                    placeholder="Select display type"
                                    options={displayOptions}
                                    value={displayFilter}
                                    onChange={setDisplayFilter}
                                />
                            </Form.Item>
                        </TableFilterControlsSection>
                    </TableFilterControlsForm>
                }
            />
            <TableAntD
                columnData={columns}
                data={filteredLessonPlans}
                loading={isLoading}
                pagination={{ pageSize: 10 }}
                dynamicWidth={true}
            />
            {/*<div>*/}
            {/*    <div className={'ch-lpm-card'}>*/}
            {/*        <div className={'ch-lpm-card-actions'}>*/}
            {/*            <Button type={'text'}>Open</Button>*/}
            {/*            <Button type={'text'}>Download</Button>*/}
            {/*        </div>*/}
            {/*    </div>*/}
            {/*</div>*/}
        </div>
    );
};

export default LessonPlanManagerDevelopment;